import { ref, nextTick } from 'vue';

/**
 * Configuration object for custom transitions
 * @interface TransitionConfig
 */
export interface TransitionConfig {
	/** Duration of the transition in milliseconds */
	duration?: number;
	/** CSS easing function (e.g., 'ease-in-out', 'ease-out', 'cubic-bezier(0.4, 0, 0.2, 1)') */
	easing?: string;
	/** Tailwind classes for enter-from state */
	enterFrom?: string;
	/** Tailwind classes for enter-to state */
	enterTo?: string;
	/** Tailwind classes for leave-from state */
	leaveFrom?: string;
	/** Tailwind classes for leave-to state */
	leaveTo?: string;
}

/**
 * Composable for managing transitions throughout the application
 * Provides utilities for creating consistent, accessible transitions
 *
 * @example
 * ```vue
 * <script setup>
 * import { useTransitions } from '@/helpers/transitions'
 *
 * const { createFadeTransition, withTransition } = useTransitions()
 *
 * // Use predefined transitions
 * const fadeClasses = createFadeTransition()
 *
 * // Wrap async operations
 * const handleClick = async () => {
 *   await withTransition(async () => {
 *     await someAsyncOperation()
 *   })
 * }
 * </script>
 *
 * <template>
 *   <transition v-bind="fadeClasses" mode="out-in">
 *     <div v-if="showContent">Content</div>
 *   </transition>
 * </template>
 * ```
 */
export const useTransitions = () => {
	/** Reactive ref indicating if a transition is currently in progress */
	const isTransitioning = ref(false);

	/**
	 * Creates transition classes for Vue's transition component
	 * Automatically handles reduced motion preferences for accessibility
	 *
	 * @param config - Transition configuration object
	 * @returns Object with transition classes for Vue's transition component
	 *
	 * @example
	 * ```typescript
	 * // Basic usage
	 * const classes = createTransitionClasses()
	 *
	 * // Custom configuration
	 * const customClasses = createTransitionClasses({
	 *   duration: 500,
	 *   easing: 'ease-out',
	 *   enterFrom: 'opacity-0 scale-95',
	 *   enterTo: 'opacity-100 scale-100'
	 * })
	 *
	 * // Use in template
	 * <transition v-bind="classes" mode="out-in">
	 *   <div v-if="show">Content</div>
	 * </transition>
	 * ```
	 */
	const createTransitionClasses = (config: TransitionConfig = {}) => {
		// Check for reduced motion preference
		const prefersReducedMotion =
			typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		// Return no transition classes if user prefers reduced motion
		if (prefersReducedMotion) {
			return {
				enterActiveClass: '',
				leaveActiveClass: '',
				enterFromClass: '',
				enterToClass: '',
				leaveFromClass: '',
				leaveToClass: '',
			};
		}

		const {
			duration = 300,
			easing = 'ease-in-out',
			enterFrom = 'opacity-0 translate-y-2',
			enterTo = 'opacity-100 translate-y-0',
			leaveFrom = 'opacity-100 translate-y-0',
			leaveTo = 'opacity-0 -translate-y-2',
		} = config;

		return {
			enterActiveClass: `transition-all duration-${duration} ${easing}`,
			leaveActiveClass: `transition-all duration-${duration} ${easing}`,
			enterFromClass: enterFrom,
			enterToClass: enterTo,
			leaveFromClass: leaveFrom,
			leaveToClass: leaveTo,
		};
	};

	/**
	 * Creates slide transition classes for horizontal or vertical sliding effects
	 * Perfect for carousels, tab switching, and modal slides
	 *
	 * @param direction - Direction of the slide animation
	 * @returns Object with slide transition classes
	 *
	 * @example
	 * ```typescript
	 * // Horizontal slide
	 * const slideClasses = createSlideTransition('right')
	 *
	 * // Vertical slide
	 * const verticalSlide = createSlideTransition('up')
	 *
	 * // Use in template
	 * <transition v-bind="slideClasses" mode="out-in">
	 *   <div v-if="showPanel">Panel content</div>
	 * </transition>
	 * ```
	 */
	const createSlideTransition = (direction: 'left' | 'right' | 'up' | 'down' = 'right') => {
		const slideConfigs = {
			left: {
				enterFrom: 'opacity-0 -translate-x-full',
				enterTo: 'opacity-100 translate-x-0',
				leaveFrom: 'opacity-100 translate-x-0',
				leaveTo: 'opacity-0 translate-x-full',
			},
			right: {
				enterFrom: 'opacity-0 translate-x-full',
				enterTo: 'opacity-100 translate-x-0',
				leaveFrom: 'opacity-100 translate-x-0',
				leaveTo: 'opacity-0 -translate-x-full',
			},
			up: {
				enterFrom: 'opacity-0 translate-y-full',
				enterTo: 'opacity-100 translate-y-0',
				leaveFrom: 'opacity-100 translate-y-0',
				leaveTo: 'opacity-0 -translate-y-full',
			},
			down: {
				enterFrom: 'opacity-0 -translate-y-full',
				enterTo: 'opacity-100 translate-y-0',
				leaveFrom: 'opacity-100 translate-y-0',
				leaveTo: 'opacity-0 translate-y-full',
			},
		};

		return createTransitionClasses({
			duration: 300,
			easing: 'ease-in-out',
			...slideConfigs[direction],
		});
	};

	/**
	 * Creates fade transition classes for simple opacity animations
	 * Ideal for modals, tooltips, and subtle content changes
	 *
	 * @returns Object with fade transition classes
	 *
	 * @example
	 * ```typescript
	 * const fadeClasses = createFadeTransition()
	 *
	 * // Use in template
	 * <transition v-bind="fadeClasses" mode="out-in">
	 *   <div v-if="showModal">Modal content</div>
	 * </transition>
	 * ```
	 */
	const createFadeTransition = () => {
		return createTransitionClasses({
			duration: 200,
			easing: 'ease-in-out',
			enterFrom: 'opacity-0',
			enterTo: 'opacity-100',
			leaveFrom: 'opacity-100',
			leaveTo: 'opacity-0',
		});
	};

	/**
	 * Creates scale transition classes for zoom-in/out effects
	 * Perfect for modals, dropdowns, and interactive elements
	 *
	 * @returns Object with scale transition classes
	 *
	 * @example
	 * ```typescript
	 * const scaleClasses = createScaleTransition()
	 *
	 * // Use in template
	 * <transition v-bind="scaleClasses" mode="out-in">
	 *   <div v-if="showDropdown">Dropdown content</div>
	 * </transition>
	 * ```
	 */
	const createScaleTransition = () => {
		return createTransitionClasses({
			duration: 250,
			easing: 'ease-out',
			enterFrom: 'opacity-0 scale-95',
			enterTo: 'opacity-100 scale-100',
			leaveFrom: 'opacity-100 scale-100',
			leaveTo: 'opacity-0 scale-95',
		});
	};

	/**
	 * Creates tab transition classes optimized for tab content switching
	 * Features subtle horizontal slide with opacity for smooth tab changes
	 *
	 * @returns Object with tab transition classes
	 *
	 * @example
	 * ```typescript
	 * const tabClasses = createTabTransition()
	 *
	 * // Use in template
	 * <transition v-bind="tabClasses" mode="out-in">
	 *   <div v-if="activeTab === 'overview'">Overview content</div>
	 *   <div v-else-if="activeTab === 'details'">Details content</div>
	 * </transition>
	 * ```
	 */
	const createTabTransition = () => {
		return createTransitionClasses({
			duration: 300,
			easing: 'ease-in-out',
			enterFrom: 'opacity-0 translate-x-4',
			enterTo: 'opacity-100 translate-x-0',
			leaveFrom: 'opacity-100 translate-x-0',
			leaveTo: 'opacity-0 -translate-x-4',
		});
	};

	/**
	 * Wraps async operations with transition state management
	 * Automatically sets isTransitioning to true during execution
	 * Useful for preventing multiple simultaneous transitions
	 *
	 * @param fn - Function to execute with transition state management
	 * @returns Promise that resolves with the function's result
	 *
	 * @example
	 * ```typescript
	 * const handleSubmit = async () => {
	 *   await withTransition(async () => {
	 *     await submitForm()
	 *     await router.push('/success')
	 *   })
	 * }
	 *
	 * // Use isTransitioning state
	 * <button :disabled="isTransitioning">
	 *   {{ isTransitioning ? 'Processing...' : 'Submit' }}
	 * </button>
	 * ```
	 */
	const withTransition = async <T>(fn: () => Promise<T> | T): Promise<T> => {
		isTransitioning.value = true;
		try {
			const result = await fn();
			await nextTick();
			return result;
		} finally {
			isTransitioning.value = false;
		}
	};

	return {
		/** Reactive ref indicating if a transition is currently in progress */
		isTransitioning: isTransitioning,
		/** Creates custom transition classes with full configuration options */
		createTransitionClasses: createTransitionClasses,
		/** Creates slide transition classes for horizontal/vertical sliding effects */
		createSlideTransition: createSlideTransition,
		/** Creates fade transition classes for simple opacity animations */
		createFadeTransition: createFadeTransition,
		/** Creates scale transition classes for zoom-in/out effects */
		createScaleTransition: createScaleTransition,
		/** Creates tab transition classes optimized for tab content switching */
		createTabTransition: createTabTransition,
		/** Wraps async operations with transition state management */
		withTransition: withTransition,
	};
};

/**
 * Predefined transition configurations for common use cases
 * Provides quick access to commonly used transition types without creating a composable instance
 *
 * @example
 * ```typescript
 * import { transitions } from '@/helpers/transitions'
 *
 * // Use predefined transitions directly
 * const fadeClasses = transitions.fade()
 * const slideClasses = transitions.slideRight()
 *
 * // Use in template
 * <transition v-bind="fadeClasses" mode="out-in">
 *   <div v-if="show">Content</div>
 * </transition>
 * ```
 */
export const transitions = {
	/** Simple fade transition for modals and subtle content changes */
	fade: () => useTransitions().createFadeTransition(),
	/** Slide transition from left to right */
	slideLeft: () => useTransitions().createSlideTransition('left'),
	/** Slide transition from right to left */
	slideRight: () => useTransitions().createSlideTransition('right'),
	/** Slide transition from bottom to top */
	slideUp: () => useTransitions().createSlideTransition('up'),
	/** Slide transition from top to bottom */
	slideDown: () => useTransitions().createSlideTransition('down'),
	/** Scale transition for modals and dropdowns */
	scale: () => useTransitions().createScaleTransition(),
	/** Tab transition optimized for tab content switching */
	tab: () => useTransitions().createTabTransition(),
};
